/**
 * The long explanation, and the analysis a move is honest enough to claim.
 *
 * ## The slot vocabulary, and why it is fragments rather than values
 *
 * A template could take `{shared}` and be handed "C and G", and then the day a move
 * shares nothing it would read "and it keeps ." So every computed slot resolves to a
 * **grammatical fragment** covering all of its cases — `{sharedPhrase}` is "holds on to
 * C and G" or "shares no note with it", never a bare list. Templates are therefore
 * written once and correct for every pair of chords they can be asked about, which is
 * what makes a phrase bank of eighty entries maintainable at all.
 *
 * ## What an explanation has to do
 *
 * It is read in the expanded branch card and in the history detail, in place of a
 * paragraph an LLM used to write. So it must genuinely explain: name what moves, name
 * what stays, and say what the chord tends to want next. The first and third of those
 * are the move's own prose; the second is computed here from the actual chords, because
 * a shared-note count written down beside a move would be wrong the first time the move
 * was applied from a chord nobody had thought about.
 */

import { mod12, spellPitchClass, type KeyContext } from '../chord/enharmonic.js';
import { LETTERS_FROM_C, type CanonicalChord, type PitchClass } from '../chord/types.js';
import type { AnalysisConfidence, BranchCurrentChord, ImpliedKey } from '../llm/schemas.js';
import type { KeyEstimate } from '../session/types.js';
import { computeFacts, notesOutsideKey, type MoveFacts } from './build.js';
import { ALL_MOVES_BY_ID } from './moves.js';
import type { HeuristicMove } from './types.js';

/**
 * How settled the key has to be before a move that reasons *from* the key is offered
 * at all. Below this the tonic is a guess, and an interval above a guess is noise.
 */
export const KEY_SETTLED_CONFIDENCE = 0.35;

/** How settled it has to be before a roman numeral is worth printing. */
export const ROMAN_NUMERAL_CONFIDENCE = 0.35;

/** And before an analysis is allowed to call itself certain. */
export const CERTAIN_KEY_CONFIDENCE = 0.5;

/** Below this, everything the engine says about the key is speculative and says so. */
export const PLAUSIBLE_KEY_CONFIDENCE = 0.25;

/** Every slot an explanation template may use. A test walks the library against this. */
export const EXPLANATION_SLOTS: readonly string[] = Object.freeze([
  'from',
  'to',
  'key',
  'motion',
  'sharedPhrase',
  'newPhrase',
  'keyPhrase',
  'patternName',
  'setupPhrase',
]);

/**
 * The slots that are computed from the real chords rather than copied from context.
 *
 * `setupPhrase` is one of them — it is built from the symbols of the tail that actually
 * matched. `patternName` is not: it is a noun phrase copied verbatim off the pattern, in
 * exactly the way `{key}` is copied off the caller.
 */
export const COMPUTED_SLOTS: readonly string[] = Object.freeze([
  'motion',
  'sharedPhrase',
  'newPhrase',
  'keyPhrase',
  'setupPhrase',
]);

/**
 * What `{patternName}` says when no pattern authored the move.
 *
 * Defined in the absent case like every other slot, because a template is written once
 * and has to be correct for every pair of chords it can be asked about — including the
 * pair a caller rebuilds from an id tomorrow, with no pattern to hand.
 */
export const NO_PATTERN_NAME = 'the phrase so far';

const INTERVAL_NAMES: readonly string[] = Object.freeze([
  'on the same root',
  'a semitone up',
  'a tone up',
  'a minor third up',
  'a major third up',
  'a fourth up',
  'a tritone away',
  'a fifth up',
  'a major third down',
  'a minor third down',
  'a tone down',
  'a semitone down',
]);

/**
 * Root motion as a musician would say it.
 *
 * Ascending up to the fifth, descending above it — because nobody hears eight semitones
 * up, they hear a major third down. The fifth itself stays "up": that is how the
 * strongest root motion in tonal music is named, whichever octave it lands in.
 */
export function motionPhrase(semitones: number): string {
  return INTERVAL_NAMES[mod12(semitones)] as string;
}

/** "C and G", "C, E and G", "G" — an English list, no serial comma. */
function nameList(names: readonly string[]): string {
  if (names.length === 0) return '';
  if (names.length === 1) return names[0] as string;
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1] as string}`;
}

/** A key as a musician names it: "C major", "E♭ minor". */
export function keyName(keyEstimate: KeyEstimate, tonicName: string): string {
  return `${tonicName} ${keyEstimate.mode}`;
}

function sharedPhrase(facts: MoveFacts): string {
  if (facts.sharedNotes.length === 0) return 'shares no note with it';
  return `holds on to ${nameList(facts.sharedNotes)}`;
}

function newPhrase(facts: MoveFacts): string {
  if (facts.newNotes.length === 0) return 'nothing in it is new';
  if (facts.newNotes.length === 1) return `${facts.newNotes[0] as string} is the new note`;
  return `${nameList(facts.newNotes)} are the new notes`;
}

function keyPhrase(facts: MoveFacts, key: string): string {
  if (facts.outsideKeyNotes.length === 0) return `every note of it belongs to ${key}`;
  if (facts.outsideKeyNotes.length === 1) {
    return `${facts.outsideKeyNotes[0] as string} comes from outside ${key}`;
  }
  return `${nameList(facts.outsideKeyNotes)} come from outside ${key}`;
}

/**
 * "with Dm7 and G7 behind it", "with G7 behind it", "from where you are".
 *
 * The setup includes the chord under the player's hands, because that chord is behind the
 * target too — it is the last step of every pattern in the table. And it names the chords
 * rather than the player: "with Dm7 and G7 behind it", never "the ii–V you set up". A
 * doodling app is not in a position to know what anybody's plan was.
 */
function setupPhrase(facts: MoveFacts): string {
  const setup = facts.setup ?? [];
  if (setup.length === 0) return 'from where you are';
  return `with ${nameList(setup)} behind it`;
}

/** The values every slot resolves to for one concrete move. */
export function slotValues(
  facts: MoveFacts,
  key: string,
  patternName?: string,
): Record<string, string> {
  return {
    from: facts.from.symbol,
    to: facts.to.symbol,
    key,
    motion: motionPhrase(facts.rootInterval),
    sharedPhrase: sharedPhrase(facts),
    newPhrase: newPhrase(facts),
    keyPhrase: keyPhrase(facts, key),
    patternName: patternName ?? NO_PATTERN_NAME,
    setupPhrase: setupPhrase(facts),
  };
}

/**
 * Fill a template.
 *
 * An unknown slot is left standing rather than blanked, because a `{typo}` on screen is
 * a bug report and an empty gap is a shrug. `library.test.ts` makes sure none ships.
 */
export function fillTemplate(template: string, values: Record<string, string>): string {
  return template.replace(/\{(\w+)\}/gu, (whole, slot: string) => values[slot] ?? whole);
}

const ROMAN_BY_DEGREE: readonly string[] = Object.freeze([
  'I',
  'II',
  'III',
  'IV',
  'V',
  'VI',
  'VII',
]);

/** Semitones above the tonic for each *letter* of the scale, by mode. */
const DIATONIC_SEMITONES: Readonly<Record<'major' | 'minor', readonly number[]>> = Object.freeze({
  major: [0, 2, 4, 5, 7, 9, 11],
  minor: [0, 2, 3, 5, 7, 8, 10],
});

// No double accidentals. A numeral like ♭♭II only ever came out of a tonic spelled on the
// wrong side of the six-sharps/six-flats seam — E♭maj7 in an E♭ minor read as D♯ minor —
// and where it is genuine the chord symbol on the card already says everything it would.
const NUMERAL_ACCIDENTALS: Readonly<Record<number, string>> = Object.freeze({
  [-1]: '♭',
  [0]: '',
  [1]: '♯',
});

/**
 * The chord's numeral in the key, or `undefined` when the key is not settled enough for
 * one to mean anything.
 *
 * Derived from the **letters**, not from the semitone distance. The two agree most of
 * the time and disagree exactly where it shows: a diminished seventh spelled G♯ in C
 * major is eight semitones above the tonic, and a semitone table would call it ♭vi while
 * the card above it says G♯. Counting letter-steps from the tonic's own spelling gives
 * ♯v°7, which is both correct and the same chord the player is looking at.
 *
 * Deliberately mechanical about *function*, though. It names where the chord sits, which
 * is always true, rather than what it is doing, which is an interpretation — so a
 * secondary dominant comes out as "II7" unless the move itself supplies the better
 * label. Being reliably plain beats being occasionally brilliant in a field read at a
 * glance.
 */
export function romanNumeralFor(
  move: HeuristicMove,
  target: CanonicalChord,
  keyEstimate: KeyEstimate,
): string | undefined {
  if (keyEstimate.confidence < ROMAN_NUMERAL_CONFIDENCE) return undefined;
  if (move.romanNumeral) return move.romanNumeral;

  const key: KeyContext = { tonicPc: keyEstimate.tonicPc, mode: keyEstimate.mode };
  const tonic = spellPitchClass(keyEstimate.tonicPc, key);
  const steps = mod7(
    LETTERS_FROM_C.indexOf(target.root.letter) - LETTERS_FROM_C.indexOf(tonic.letter),
  );

  const diatonic = (DIATONIC_SEMITONES[keyEstimate.mode] as readonly number[])[steps] as number;
  let alteration = mod12(target.rootPc - keyEstimate.tonicPc) - diatonic;
  if (alteration > 6) alteration -= 12;
  if (alteration < -6) alteration += 12;

  const accidental = NUMERAL_ACCIDENTALS[alteration];
  // Beyond a single accidental the numeral is not a numeral anybody can read, and the
  // chord symbol on the card says everything the numeral would have.
  if (accidental === undefined) return undefined;

  const base = ROMAN_BY_DEGREE[steps] as string;
  const lower = target.quality === 'minor' || target.quality === 'diminished';
  const numeral = lower ? base.toLowerCase() : base;

  const inversion = target.bassPc === target.rootPc ? '' : inversionFigure(target);
  // An inverted seventh's figure already says "seventh chord": V65, never V765. The
  // quality marks stay, because they are the part the figure does not carry — vii°65,
  // iiø43, Imaj42.
  const suffix = inversion ? numeralSuffix(target).replace(/7$/, '') : numeralSuffix(target);
  return `${accidental}${numeral}${suffix}${inversion}`;
}

function mod7(n: number): number {
  return ((n % 7) + 7) % 7;
}

function numeralSuffix(chord: CanonicalChord): string {
  if (chord.quality === 'diminished') {
    if (chord.seventh === 'dim7') return '°7';
    if (chord.seventh === 'min7') return 'ø7';
    return '°';
  }
  if (chord.quality === 'augmented') return '+';
  // A suspension keeps its name with a seventh on it: V7sus4, never a bare V7 under a card
  // that reads G7sus4 and has no third at all.
  if (chord.quality === 'sus4' || chord.quality === 'sus2') {
    return `${chord.seventh === 'min7' ? '7' : ''}${chord.quality}`;
  }
  if (chord.seventh === 'maj7') return 'maj7';
  if (chord.seventh === 'min7') return '7';
  return '';
}

/** Figured-bass shorthand for the inversions a move can actually produce. */
function inversionFigure(chord: CanonicalChord): string {
  if (chord.seventh) return chord.inversion === 1 ? '65' : chord.inversion === 3 ? '42' : '43';
  return chord.inversion === 1 ? '6' : '64';
}

/**
 * How much authority this analysis has earned.
 *
 * Three inputs and no cleverness: an interpretive move is never certain however settled
 * the key is, because "that is a chromatic mediant" is a reading and there is always a
 * second one; a move that stays inside a confident key is certain, because the numeral
 * and the relation are then simply facts; everything else is plausible, and a key nobody
 * has established makes the whole analysis speculative and says so.
 */
export function confidenceFor(
  move: HeuristicMove,
  target: CanonicalChord,
  keyEstimate: KeyEstimate,
): AnalysisConfidence {
  if (keyEstimate.confidence < PLAUSIBLE_KEY_CONFIDENCE) return 'speculative';
  if (move.interpretive) return 'plausible';
  if (
    keyEstimate.confidence >= CERTAIN_KEY_CONFIDENCE &&
    notesOutsideKey(target, keyEstimate).length === 0
  ) {
    return 'certain';
  }
  return 'plausible';
}

/** The key a move implies, if it implies one. `tonic` is a note name, not a pitch class. */
export function impliedKeyFor(
  move: HeuristicMove,
  target: CanonicalChord,
  spell: (pc: PitchClass) => string,
): ImpliedKey | undefined {
  if (!move.impliesKey) return undefined;
  return {
    tonic: spell(mod12(target.rootPc + move.impliesKey.semitones)),
    mode: move.impliesKey.mode,
    certainty: move.impliesKey.certainty,
  };
}

/** What `explainMove` needs to say something true. */
export interface ExplainContext {
  current: BranchCurrentChord;
  target: CanonicalChord;
  keyEstimate: KeyEstimate;
  /** The key's tonic as a note name, e.g. "E♭". Spelled by the caller, in the key. */
  keyTonicName: string;
  /**
   * The tail a pattern matched, oldest first and including the current chord, for a
   * caller rebuilding a pattern-authored paragraph. The live path never needs either of
   * these — the client stores the resolved text — so they are for the sketch reopened
   * tomorrow, and both slots read correctly without them.
   */
  setup?: readonly string[];
  patternName?: string;
}

/**
 * The resolved explanation for one move, made concrete against one pair of chords.
 *
 * ## The seam, and why it takes a move id rather than a suggestion
 *
 * `generateHeuristicBranchSet` already returns this string on every branch, so the
 * common path never calls this function — the explanation arrives with the suggestion
 * and there is nothing to look up. This exists for the caller that kept only the id:
 * a sketch reopened tomorrow, a history detail asked about a chord three moves back.
 *
 * It takes a `moveId` rather than a `BranchSuggestion` because a suggestion cannot
 * identify the move that produced it. Two library moves may legitimately land on the
 * same chord with the same movement kind read two different ways — that is the point of
 * a library rather than a table — so recovering the move from the chord would be a guess
 * dressed as a lookup. Returns `null` for an id the library does not know, which is what
 * a renamed move looks like from a cache written before the rename.
 *
 * It resolves against `ALL_MOVES_BY_ID` rather than `MOVES_BY_ID`, so a `landing/` or
 * `chain/` id written by the pattern layer comes back exactly as a library id does. Pass
 * `setup` and `patternName` alongside to rebuild the paragraph word for word; without
 * them the two pattern slots still read, because both are defined in the absent case.
 */
export function explainMove(moveId: string, context: ExplainContext): string | null {
  const move = ALL_MOVES_BY_ID.get(moveId);
  if (!move) return null;
  const facts = computeFacts(context.current, context.target, context.keyEstimate, context.setup);
  const key = keyName(context.keyEstimate, context.keyTonicName);
  return fillTemplate(move.phrases.explanation, slotValues(facts, key, context.patternName));
}
